import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Wrench, Shield, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CallButton } from './CallButton';
import { TopAnnouncementBar } from './TopAnnouncementBar';

export const AudiRepair = () => {
  const audiServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Advanced Audi engine diagnostics using specialized VCDS diagnostic tools",
      icon: Wrench
    },
    {
      title: "Transmission Service",
      description: "Expert Audi S tronic and Tiptronic transmission repair and maintenance",
      icon: Wrench
    },
    {
      title: "Brake System Service",
      description: "Complete Audi brake inspection, pad replacement, and rotor servicing",
      icon: Shield
    },
    {
      title: "Electrical Systems",
      description: "Audi electrical diagnostics and MMI system repair",
      icon: Wrench
    },
    {
      title: "Suspension & Steering",
      description: "Audi air suspension repair and Quattro system maintenance",
      icon: Wrench
    },
    {
      title: "Oil Change & Maintenance",
      description: "Audi-specific oil changes and routine maintenance services",
      icon: Clock
    },
    {
      title: "AC System Repair",
      description: "Audi air conditioning service and refrigerant recharge",
      icon: Wrench
    },
    {
      title: "Quattro System Service",
      description: "Audi Quattro all-wheel drive system diagnostics and repair",
      icon: Wrench
    }
  ];

  const audiModels = [
    "Audi A3", "Audi A4", "Audi A5", "Audi A6",
    "Audi A7", "Audi A8", "Audi Q3", "Audi Q5",
    "Audi Q7", "Audi Q8", "Audi TT", "Audi R8",
    "Audi S3", "Audi S4", "Audi S5", "Audi S6",
    "Audi RS3", "Audi RS4", "Audi RS5", "Audi RS6",
    "Audi RS7", "Audi e-tron", "Audi Q2", "Audi SQ5"
  ];

  const whyChooseUs = [
    {
      title: "Audi-Certified Technicians",
      description: "Our mechanics are specially trained and certified for Audi repairs"
    },
    {
      title: "Genuine Audi Parts",
      description: "We use OEM and genuine Audi parts for all repairs"
    },
    {
      title: "VCDS Diagnostic Tools",
      description: "State-of-the-art Audi VCDS diagnostic equipment"
    },
    {
      title: "Quattro Expertise",
      description: "Specialists in Audi Quattro all-wheel drive systems"
    }
  ];

  const openWhatsApp = () => {
    const message = "Hi Think Mechanic, I need Audi repair service and want to claim my 15% website discount!";
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Audi Repair Dubai | Audi Service & Maintenance | Think Mechanic</title>
        <meta name="description" content="Specialized Audi repair and maintenance in Dubai. Expert diagnostics, Quattro service, transmission, AC and engine repairs for all Audi models." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/audi-repair-dubai" />
        <meta name="keywords" content="Audi repair Dubai, Audi service Dubai, Audi garage Dubai, Audi workshop Dubai, Audi mechanic Dubai, Audi specialist Dubai, Audi garage Ras Al Khor, Audi specialist Ras Al Khor, Audi garage near me, Audi mechanic near me, Audi AC repair Dubai, Audi oil change Dubai, Audi brake repair Dubai, Audi engine repair Dubai, Audi transmission repair Dubai, Audi Quattro service Dubai, Audi maintenance Dubai, Audi diagnostic Dubai, Audi repair near me, Best Audi garage Dubai, Audi service center Dubai, Affordable Audi repair Dubai, Audi oil change near me, Audi brake service Dubai, Audi S tronic service, Audi suspension repair Dubai, Audi electrical repair Dubai, Audi battery replacement Dubai, Think Mechanic Audi, Audi A4 repair Dubai, Audi A6 repair Dubai, Audi Q5 repair Dubai, Audi repair Ras Al Khor, Emergency Audi repair Dubai, Same day Audi service, Audi MMI repair Dubai, Audi check engine light Dubai, Certified Audi technician Dubai, Independent Audi garage Dubai, Trusted Audi mechanic Dubai, Audi A3 service Dubai, Audi A5 repair Dubai, Audi Q7 repair Dubai, Audi S4 service Dubai, Audi RS5 repair Dubai, Audi TT service Dubai, Audi R8 repair Dubai, Audi e-tron repair Dubai, Out of warranty Audi repair, Audi extended warranty Dubai, Audi pre-purchase inspection, Used Audi inspection Dubai, Top Audi garage Dubai, Audi service Al Quoz, Audi repair Sharjah, car AC repair Dubai, auto AC repair near me" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Audi Repair Dubai | Expert Audi Service | Think Mechanic" />
        <meta property="og:description" content="Professional Audi repair in Dubai. Certified technicians, genuine parts, 15+ years experience. All Audi models serviced." />
        <meta property="og:url" content="https://thinkmechanicdxb.com/audi-repair-dubai" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Audi Repair Dubai | Think Mechanic" />
        <meta name="twitter:description" content="Expert Audi repair and maintenance in Dubai. Certified technicians, genuine parts." />
      </Helmet>

      {/* Top Announcement Bar */}
      <TopAnnouncementBar />

      {/* Same Header as Homepage */}
      <Header />

      {/* Hero Section with Audi Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Audi Background Image - Audi in professional workshop */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1743264533231-be084982c6e4?w=1920&q=85"
            alt="Audi Car in Professional Workshop - Think Mechanic Dubai"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-32">
            {/* Audi Logo Badge */}
            <div className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md border-2 border-white/20 px-6 py-4 rounded-full mb-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/iakub0i6_image.png"
                  alt="Audi Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-gray-900 font-bold text-xl">Audi</span>
                <span className="text-white/80 text-sm font-semibold">Repair Specialists</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Audi Repair in
              <span className="text-red-600"> Dubai</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Specialized Audi repair and maintenance in Ras Al Khor. Trust certified technicians with your luxury vehicle.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={openWhatsApp}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50 group"
              >
                Book Audi Service & Get 15% OFF
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-white/90 text-sm">✨ Special discount for website visitors</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">550+</div>
                <div className="text-gray-300 text-sm">Audis Serviced</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-300 text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"></div>
      </section>

      {/* Audi Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Audi Repair Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From routine maintenance to Quattro system repairs, we handle all Audi service needs with expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiServices.map((service, index) => (
              <Card key={index} className="bg-white border-gray-200 p-6 hover:border-red-600 transition-all duration-300">
                <div className="bg-red-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Think Mechanic for Audi Repair?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audi Models We Service */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Audi Models We Service
            </h2>
            <p className="text-gray-600 text-lg">
              We service all Audi models, from A-series to R8 supercars
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {audiModels.map((model, index) => (
              <div key={index} className="bg-white border border-gray-200 px-4 py-3 rounded-lg text-center hover:border-red-600 transition-all duration-300">
                <span className="text-gray-900 font-semibold">{model}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">+ All other Audi models</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready to Service Your Audi?
          </h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
            Book your Audi service today and get 15% off. Our expert technicians are ready to help!
          </p>
          
          <Button
            onClick={openWhatsApp}
            className="bg-white hover:bg-gray-100 text-red-600 px-10 py-6 rounded-lg text-xl font-bold transition-all duration-200 shadow-2xl hover:scale-105"
          >
            Book Now via WhatsApp
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-900">
            <div className="flex items-center space-x-2">
              <span className="font-semibold">📞 056 667 1468</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30"></div>
            <div>
              <span>📍 Ras Al Khor Industrial Area 1, Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Call Button */}
      <CallButton />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};
